import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './GameViewportModal.css';

/**
 * GameViewportModal — Cluster 1 (map-viewport) modal primitive.
 *
 * Owns the boilerplate every player-facing modal needs:
 *   - Reactive portal target hunt for `.deck-map-area` (RAF-retried, body fallback)
 *   - Body scroll lock while open
 *   - Escape-to-close (suppressed when `lockScrimClose` is true)
 *   - Click-scrim-to-close (suppressed when `lockScrimClose` is true)
 *   - Focus management — close button takes focus on open
 *   - Animated mount via CSS keyframes on the base classes
 *
 * Consumers pass their own content as children and a `variantClassName` that
 * gets suffixed onto the base classes so each modal can keep its colour
 * palette (e.g. variantClassName="player-shop" → `gvm-overlay--player-shop`).
 *
 * Replaces the per-orb hand-rolled portal+scrim+animation duplicated 8× today
 * (InfectionOrb, VaccineOrb, ReckoningOrb, MoleculeOrb, PvPOrb, AskOrb,
 * AchievementsOrb, MapOrbTray panels). See `wiki/decisions/modal-canon.md`
 * for the Cluster 1 contract this primitive enforces.
 */
export default function GameViewportModal({
  open,
  onClose,
  ariaLabel,
  variantClassName = '',
  lockScrimClose = false,
  hideCloseButton = false,
  /** Optional element rendered as the panel's header strip. */
  header = null,
  /** Children = panel body content (sits below header if present). */
  children,
  /** Externally-provided ref to receive focus when the modal opens. */
  initialFocusRef = null,
  /** Inline style applied to the .gvm-body panel — primary path for
   *  CSS-variable scoping (e.g. EquipmentViewportModal's keystone-accent
   *  --evm-accent-rgb that header + body descendants both read). */
  bodyStyle = null,
}) {
  const closeButtonRef = useRef(null);
  const [target, setTarget] = useState(null);

  const closeIfUnlocked = useCallback(() => {
    if (lockScrimClose) return;
    onClose?.();
  }, [lockScrimClose, onClose]);

  // Body scroll lock + Escape handler + initial focus
  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTarget = initialFocusRef?.current || closeButtonRef.current;
    focusTarget?.focus?.();

    const onKey = (event) => {
      if (event.key === 'Escape') closeIfUnlocked();
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, closeIfUnlocked, initialFocusRef]);

  // Portal target — find `.deck-map-area` with RAF retry, body fallback
  useEffect(() => {
    if (!open || typeof document === 'undefined') {
      setTarget(null);
      return undefined;
    }
    let cancelled = false;
    let attempts = 0;
    let raf;
    const find = () => {
      if (cancelled) return;
      const el = document.querySelector('.deck-map-area') || document.body;
      setTarget(el);
      if (el === document.body && attempts < 60) {
        attempts += 1;
        raf = requestAnimationFrame(find);
      }
    };
    find();
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [open]);

  if (!open || !target) return null;

  const viewportSuffix = variantClassName ? ` gvm-viewport--${variantClassName}` : '';
  const variantSuffix = variantClassName ? ` gvm-overlay--${variantClassName}` : '';
  const bodySuffix = variantClassName ? ` gvm-body--${variantClassName}` : '';

  const panel = (
    <div className={`gvm-viewport${viewportSuffix}`}>
      <div
        className={`gvm-overlay${variantSuffix}`}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        onClick={closeIfUnlocked}
      >
        <div
          className={`gvm-body${bodySuffix}`}
          style={bodyStyle || undefined}
          onClick={(e) => e.stopPropagation()}
        >
          {!hideCloseButton && (
            <button
              ref={closeButtonRef}
              className="gvm-close"
              onClick={onClose}
              aria-label="Close"
              type="button"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
          )}
          {header}
          <div className={`gvm-content${variantClassName ? ` gvm-content--${variantClassName}` : ''}`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(panel, target);
}
